import { useState, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { InteractiveModal } from 'components/Modals/InteractiveModal/BaseModal';
import { MultiSelector } from 'components/Form/Selectors/MultiSelector';
import { type SelectChangeEvent } from '@mui/material';
import { type TMultiSelectOptions } from 'types/utils.types';
import useAppTheme from 'hooks/useAppTheme';
import ReportAPI from 'api/report.api';
import { ThemePalette } from 'mui.theme';
import { useAppSelector, useAppDispatch } from 'store/hook';
import { type CrewHaulageExportPayload } from 'api/report.api';
import { showNotification } from 'store/notification.slice';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

interface ShareReportModalProps {
  open: boolean;
  reportId: string;
  reportName: string;
  basePayload: Omit<
    CrewHaulageExportPayload,
    | 'action'
    | 'fileType'
    | 'toRecipients'
    | 'bccRecipients'
    | 'userRolesToEmail'
  > | null;
  handleAbort: () => void;
  onSuccess?: () => void;
}

const ShareReportModal = ({
  open,
  reportName,
  basePayload,
  handleAbort,
  onSuccess,
}: ShareReportModalProps) => {
  const { t } = useTranslation();
  const { enableDarkTheme } = useAppTheme();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const reportsAPI = new ReportAPI();
  const dispatch = useAppDispatch();

  const userRole = useAppSelector((state) => state.authReducer.role);
  const { externalUserList } = useAppSelector(
    (state) => state.externalUserReducer
  );
  const { internalUserList } = useAppSelector(
    (state) => state.internalUserReducer
  );

  const [toRecipients, setToRecipients] = useState<TMultiSelectOptions>([]);
  const [bccRecipients, setBccRecipients] = useState<TMultiSelectOptions>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Build email options from external and internal users
  const recipientOptions: TMultiSelectOptions = useMemo(() => {
    const mapToOption = (user: { email?: string | null }) =>
      user.email
        ? {
            id: user.email,
            display: user.email,
          }
        : null;

    if (userRole === 'external-user' || userRole === 'external-admin') {
      return externalUserList
        .map(mapToOption)
        .filter((opt): opt is { id: string; display: string } => opt !== null);
    }

    return (
      [
        ...externalUserList.map(mapToOption),
        ...internalUserList.map(mapToOption),
      ].filter((opt): opt is { id: string; display: string } => opt !== null) ??
      []
    );
  }, [userRole, externalUserList, internalUserList]);

  const isValidForm = useMemo(() => {
    return basePayload !== null && toRecipients.length > 0;
  }, [basePayload, toRecipients]);

  const handleToRecipientsSelectOne = (event: SelectChangeEvent<any>) => {
    const selectedValues = event.target.value as string[];
    const parsedValues: TMultiSelectOptions = selectedValues.map((val) =>
      JSON.parse(val)
    );
    setToRecipients(parsedValues);
  };

  const handleToRecipientsSelectAll = () => {
    if (toRecipients.length === recipientOptions.length) {
      setToRecipients([]);
    } else {
      setToRecipients([...recipientOptions]);
    }
  };

  const handleBccRecipientsSelectOne = (event: SelectChangeEvent<any>) => {
    const selectedValues = event.target.value as string[];
    const parsedValues: TMultiSelectOptions = selectedValues.map((val) =>
      JSON.parse(val)
    );
    setBccRecipients(parsedValues);
  };

  const handleBccRecipientsSelectAll = () => {
    if (bccRecipients.length === recipientOptions.length) {
      setBccRecipients([]);
    } else {
      setBccRecipients([...recipientOptions]);
    }
  };

  const renderRecipientsValue = (selected: TMultiSelectOptions) => {
    if (selected.length === 0) {
      return 'Select...';
    }
    if (selected.length === 1) {
      return selected[0].display;
    }
    return `${selected.length} selected`;
  };

  const resetForm = () => {
    setToRecipients([]);
    setBccRecipients([]);
  };

  const handleSubmit = async () => {
    if (!isValidForm || basePayload === null) return;

    setIsSubmitting(true);
    try {
      await reportsAPI.exportCrewHaulageForEmail({
        ...basePayload,
        action: 'email',
        fileType: 'pdf',
        toRecipients: toRecipients.map((r) => r.id),
        bccRecipients:
          bccRecipients.length > 0 ? bccRecipients.map((r) => r.id) : [],
        userRolesToEmail: [],
      });

      resetForm();

      dispatch(
        showNotification({
          message: t('report_shared_successfully'),
          type: 'success',
        })
      );

      // Call success callback if provided
      if (onSuccess) {
        onSuccess();
      }

      // Close modal
      handleAbort();
    } catch (error) {
      const message = await ReportAPI.extractErrorMessage(error);

      console.error('Error sharing report:', error);
      dispatch(
        showNotification({
          message,
          type: 'error',
        })
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <InteractiveModal
      initialState={open}
      SuccessButtonProps={{
        disabled: !isValidForm || isSubmitting,
        onClick: handleSubmit,
        label: isSubmitting ? 'Sharing...' : 'SHARE',
      }}
      handleAbort={() => {
        resetForm();
        handleAbort();
      }}
      title={`Share Report: `}
      enableDarkTheme={enableDarkTheme}
      ModalContentProps={{
        gridItemProps: {
          lg: 18,
        },
      }}
    >
      {/* To Recipients */}
      <Box
        key="toRecipients"
        sx={{
          mt: 1,
        }}
      >
        <Typography
          variant="bodyBold"
          display="block"
          mb={'4px'}
          sx={{
            color: palette.textPrimary,
          }}
        >
          {t('to_recipients')}
        </Typography>
        <MultiSelector
          selectorOptions={recipientOptions}
          value={toRecipients}
          handleSelectOne={handleToRecipientsSelectOne}
          handleSelectAll={handleToRecipientsSelectAll}
          renderValue={renderRecipientsValue}
          labelText="Select recipients"
          enableDarkTheme={enableDarkTheme}
          minWidth="100%"
          height="37px"
          formControlSx={{
            width: '100%',
          }}
          isSelectionEmpty={recipientOptions.length === 0}
          messageIfSelectionEmpty="No recipients available"
          serializeValues={true}
        />
      </Box>

      {/* BCC Recipients */}
      <Box
        key="bccRecipients"
        sx={{
          mt: 1,
        }}
      >
        <Typography
          variant="bodyBold"
          display="block"
          mb={'4px'}
          sx={{
            color: palette.textPrimary,
          }}
        >
          {t('bcc_recipients')}
        </Typography>
        <MultiSelector
          selectorOptions={recipientOptions}
          value={bccRecipients}
          handleSelectOne={handleBccRecipientsSelectOne}
          handleSelectAll={handleBccRecipientsSelectAll}
          renderValue={renderRecipientsValue}
          labelText="Select BCC recipients (optional)"
          enableDarkTheme={enableDarkTheme}
          minWidth="100%"
          height="37px"
          formControlSx={{
            width: '100%',
          }}
          isSelectionEmpty={recipientOptions.length === 0}
          messageIfSelectionEmpty="No recipients available"
          serializeValues={true}
        />
      </Box>
    </InteractiveModal>
  );
};

export default ShareReportModal;
